import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { brand, shadows } from '@/app/theme/tokens';
import type { AssistantCards, AssistantGuideCard } from '../types';

const GUIDE_PATH = /^\/guides\/[a-f\d]{24}$/i;

function allowedPath(path: string): string | null {
  if (path === '/destinations' || path === '/my-bookings' || GUIDE_PATH.test(path)) return path;
  return null;
}

function place(city?: string, state?: string): string | null {
  const parts = [city, state].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function priceLabel(card: AssistantGuideCard): string | null {
  if (card.priceFrom == null) return null;
  const currency = card.currency === 'INR' || !card.currency ? '₹' : `${card.currency} `;
  return `From ${currency}${card.priceFrom.toLocaleString('en-IN')}`;
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (letter) => letter.toUpperCase());
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

const cardShell = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  p: 1.15,
  borderRadius: '12px',
  textDecoration: 'none',
  color: brand.textPrimary,
  bgcolor: brand.white,
  border: `1px solid ${brand.border}`,
  boxShadow: shadows.xs,
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
  '&:hover': {
    borderColor: brand.blue200,
    boxShadow: shadows.cardHover,
    transform: 'translateY(-1px)',
  },
} as const;

interface AssistantCardsViewProps {
  cards: AssistantCards;
  onOpenPath: (path: string) => void;
}

export function AssistantCardsView({ cards, onOpenPath }: AssistantCardsViewProps) {
  const guides = cards.guides ?? [];
  const destinations = cards.destinations ?? [];
  const bookings = cards.bookings ?? [];
  if (!guides.length && !destinations.length && !bookings.length) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85, mt: 0.85 }}>
      {guides.map((card) => {
        const path = allowedPath(card.path);
        if (!path) return null;
        const where = place(card.city, card.state);
        const price = priceLabel(card);
        return (
          <Box
            key={`guide-${card.id}`}
            component={RouterLink}
            to={path}
            onClick={() => onOpenPath(path)}
            sx={cardShell}
          >
            {card.photo ? (
              <Box
                component="img"
                src={card.photo}
                alt=""
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                  const fallback = event.currentTarget.nextElementSibling as HTMLElement | null;
                  if (fallback) fallback.style.display = 'grid';
                }}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '10px',
                  objectFit: 'cover',
                  flexShrink: 0,
                  bgcolor: brand.backgroundAlt,
                }}
              />
            ) : null}
            <Box
              aria-hidden
              sx={{
                width: 56,
                height: 56,
                borderRadius: '10px',
                flexShrink: 0,
                bgcolor: brand.navy,
                color: brand.white,
                display: card.photo ? 'none' : 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: '0.9rem',
              }}
            >
              {initials(card.name) || 'G'}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {card.name}
              </Typography>
              {where ? (
                <Typography variant="caption" sx={{ color: brand.textSecondary, display: 'block' }}>
                  {where}
                </Typography>
              ) : null}
              {card.languages.length > 0 ? (
                <Typography variant="caption" sx={{ color: brand.textMuted, display: 'block' }}>
                  {card.languages.slice(0, 3).join(' · ')}
                </Typography>
              ) : null}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.35, flexWrap: 'wrap' }}>
                {typeof card.rating === 'number' ? (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                    <StarRoundedIcon sx={{ fontSize: 14, color: brand.gold }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: brand.textPrimary }}>
                      {card.rating.toFixed(1)}
                    </Typography>
                  </Box>
                ) : null}
                {price ? (
                  <Typography variant="caption" sx={{ fontWeight: 700, color: brand.blue700 }}>
                    {price}
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: brand.textMuted }}>
                    Published profile
                  </Typography>
                )}
              </Box>
            </Box>
            <ChevronRightRoundedIcon sx={{ color: brand.blue600, fontSize: 20, flexShrink: 0 }} />
          </Box>
        );
      })}
      {destinations.map((card) => (
        <Box
          key={`destination-${card.id}`}
          component={RouterLink}
          to="/destinations"
          onClick={() => onOpenPath('/destinations')}
          sx={cardShell}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '10px',
              bgcolor: brand.blue50,
              color: brand.blue700,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <PlaceOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {card.name}
            </Typography>
            {card.country ? (
              <Typography variant="caption" sx={{ color: brand.textSecondary }}>
                {card.country}
              </Typography>
            ) : null}
          </Box>
          <ChevronRightRoundedIcon sx={{ color: brand.blue600, fontSize: 20, flexShrink: 0 }} />
        </Box>
      ))}
      {bookings.map((card) => (
        <Box
          key={`booking-${card.id}`}
          component={RouterLink}
          to="/my-bookings"
          onClick={() => onOpenPath('/my-bookings')}
          sx={cardShell}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '10px',
              bgcolor: brand.goldXlight,
              color: brand.goldDark,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <EventAvailableOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {card.destination || 'Booking'}
            </Typography>
            <Typography variant="caption" sx={{ color: brand.textSecondary, display: 'block' }}>
              {[statusLabel(card.status), card.travelDate, card.guideName].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <ChevronRightRoundedIcon sx={{ color: brand.blue600, fontSize: 20, flexShrink: 0 }} />
        </Box>
      ))}
    </Box>
  );
}
